/**
 * Место съёмки для фото и видео отчёта (ПР-03, МП-07): только после явного согласия,
 * один раз на запись, без фонового слежения. Из галереи место не берём.
 */
import type { SitePhotoLocation } from '@epc/contracts';

export type LocationReader = () => Promise<SitePhotoLocation | null>;

/** Запрос координат браузера; при отказе или ошибке — null (запись без места). */
export const readBrowserLocation: LocationReader = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracyM: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
          at: new Date(pos.timestamp || Date.now()).toISOString(),
        });
      },
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 0 },
    );
  });

/**
 * Спросить согласие и при «да» получить координаты.
 * Без согласия или без координат — { locationConsent: false, location: null }.
 * @param kind фото или видео — только текст вопроса; логика одна.
 */
export async function askPhotoLocation(
  confirm: (message: string) => boolean,
  read: LocationReader = readBrowserLocation,
  kind: 'photo' | 'video' = 'photo',
): Promise<{ locationConsent: boolean; location: SitePhotoLocation | null }> {
  const noun = kind === 'video' ? 'видео' : 'фото';
  const ok = confirm(
    `Сохранить место съёмки на этом ${noun}? Место пишется только в запись отчёта, фоном телефон не отслеживается.`,
  );
  if (!ok) return { locationConsent: false, location: null };
  const location = await read();
  if (!location) return { locationConsent: false, location: null };
  return { locationConsent: true, location };
}
